import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, PackageX, Loader2 } from "lucide-react";
import UploadButton, { fileUrl } from "@/components/UploadButton";
import { CATEGORIES, categoryLabel } from "@/lib/categories";

const EMPTY = {
  name: "", slug: "", sku: "", category: "chopping_board", subcategory: "", price: 0,
  on_sale: false, sale_price: null, perfect_gift: false, free_shipping: false,
  description: "", short_description: "", images: [], video: "", materials: "",
  dimensions: "", stock: 1, featured: false, active: true,
};
const MAX_IMAGES = 3;

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [remakeCounts, setRemakeCounts] = useState({});
  const [shippingClasses, setShippingClasses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [imgInput, setImgInput] = useState("");
  const [soldOutOnly, setSoldOutOnly] = useState(false);
  const [skuFilter, setSkuFilter] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const load = () => Promise.all([
    api.get("/admin/products").then((r) => setProducts(r.data)),
    api.get("/admin/remake-request-counts").then((r) => setRemakeCounts(r.data)).catch(() => {}),
    api.get("/shipping-classes").then((r) => setShippingClasses(r.data)).catch(() => {}),
  ]);

  useEffect(() => { load(); }, []);

  // Sort: sold-out first, then by updated_at (falls back to created_at) descending.
  // Filter: optionally restrict to sold-out only, and/or by SKU substring (case-insensitive).
  const displayed = useMemo(() => {
    const sku = skuFilter.trim().toLowerCase();
    let list = soldOutOnly
      ? products.filter((p) => (p.stock ?? 0) < 1)
      : [...products];
    if (sku) {
      list = list.filter((p) => (p.sku || "").toLowerCase().includes(sku));
    }
    return list.sort((a, b) => {
      const aOut = (a.stock ?? 0) < 1 ? 1 : 0;
      const bOut = (b.stock ?? 0) < 1 ? 1 : 0;
      if (aOut !== bOut) return bOut - aOut; // sold-out first
      const aT = a.updated_at || a.created_at || "";
      const bT = b.updated_at || b.created_at || "";
      return bT.localeCompare(aT); // newest first
    });
  }, [products, soldOutOnly, skuFilter]);

  const soldOutCount = useMemo(
    () => products.filter((p) => (p.stock ?? 0) < 1).length,
    [products]
  );

  // Existing subcategories within the currently selected product's main category — used for the datalist suggestions
  const subcatSuggestions = (() => {
    if (!editing) return [];
    const set = new Set();
    products
      .filter((p) => p.category === editing.category && p.subcategory)
      .forEach((p) => set.add(p.subcategory));
    return Array.from(set).sort();
  })();

  const startCreate = () => setEditing({ ...EMPTY });
  const startEdit = (p) => setEditing({ ...p });

  const save = async () => {
    // Guard against double-submits (mobile users tapping Save multiple times before
    // the network round-trip returns).
    if (isSubmitting) return;
    // Sanitize sale fields to prevent invalid combinations from being persisted
    const payload = { ...editing };
    if (!payload.on_sale) {
      payload.sale_price = null;
    } else if (payload.sale_price == null || Number(payload.sale_price) <= 0 || Number(payload.sale_price) >= Number(payload.price || 0)) {
      toast.error("Sale price must be a positive number lower than the original price.");
      return;
    }
    payload.images = (payload.images || []).slice(0, MAX_IMAGES);
    setIsSubmitting(true);
    try {
      if (!payload.id) {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      } else {
        const { id, ...body } = payload;
        await api.put(`/admin/products/${id}`, body);
        toast.success("Product updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setIsSubmitting(false);
    }
  };

  const del = async (p) => {
    if (!confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const set = (k, v) => setEditing((s) => ({ ...s, [k]: v }));

  const slugify = (str) =>
    (str || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const onNameChange = (value) => {
    setEditing((s) => {
      const next = { ...s, name: value };
      // Auto-fill slug only if the user hasn't customised it yet
      const autoFromOld = slugify(s.name || "");
      if (!s.slug || s.slug === autoFromOld) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const addImg = () => {
    if (!imgInput.trim()) return;
    const current = editing.images || [];
    if (current.length >= MAX_IMAGES) return;
    set("images", [...current, imgInput.trim()].slice(0, MAX_IMAGES));
    setImgInput("");
  };
  const rmImg = (i) => set("images", editing.images.filter((_, idx) => idx !== i));

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display text-4xl text-[#F5F5DC]">Products</h1>
        <button onClick={startCreate} className="btn-primary" data-testid="new-product-btn">
          <Plus size={14} /> New Product
        </button>
      </div>

      {/* SKU filter + sold-out toggle */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <input
            type="text"
            value={skuFilter}
            onChange={(e) => setSkuFilter(e.target.value)}
            placeholder="Filter by code (e.g. HW-01, A, E1)"
            data-testid="sku-filter-input"
            className="w-full h-14 min-h-[48px] px-5 pr-12 bg-[#0A0A0A] border-2 border-[#2A2A2A] rounded-full text-[#F5F5DC] placeholder-[#6E6E6E] text-base font-bold tracking-wide focus:border-[#B87333] focus:outline-none transition-colors touch-manipulation"
          />
          {skuFilter && (
            <button
              type="button"
              onClick={() => setSkuFilter("")}
              data-testid="sku-filter-clear"
              aria-label="Clear SKU filter"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-[#F5F5DC] p-1"
            >
              <X size={18} />
            </button>
          )}
        </div>
        {skuFilter && (
          <span className="text-xs uppercase tracking-widest text-[#B87333]" data-testid="sku-filter-count">
            {displayed.length} match{displayed.length === 1 ? "" : "es"}
          </span>
        )}
        <button
          type="button"
          onClick={() => setSoldOutOnly((v) => !v)}
          aria-pressed={soldOutOnly}
          data-testid="filter-sold-out-toggle"
          className={`inline-flex items-center gap-2 h-14 min-h-[48px] px-5 sm:px-6 border-2 rounded-full transition-all active:scale-[0.97] touch-manipulation ${
            soldOutOnly
              ? "bg-[#B87333] border-[#B87333] text-[#0A0A0A] shadow-lg"
              : "bg-[#0A0A0A] border-[#2A2A2A] text-[#F5F5DC] hover:border-[#B87333]"
          }`}
        >
          <PackageX size={20} strokeWidth={2.5} />
          <span className="font-display text-base sm:text-lg font-bold tracking-wide">
            {soldOutOnly ? "Showing Sold Out Only" : "Show Sold Out Only"}
          </span>
          <span
            className={`ml-1 px-2.5 py-0.5 text-xs font-black tabular-nums rounded-full ${
              soldOutOnly ? "bg-[#0A0A0A] text-[#B87333]" : "bg-[#2A2A2A] text-[#F5F5DC]"
            }`}
            data-testid="sold-out-count"
          >
            {soldOutCount}
          </span>
        </button>
        {soldOutOnly && (
          <button
            type="button"
            onClick={() => setSoldOutOnly(false)}
            className="text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-[#F5F5DC] underline underline-offset-4"
            data-testid="filter-clear"
          >
            Show all products
          </button>
        )}
      </div>

      {/* Desktop / tablet table */}
      <div className="hidden md:block bg-[#111] border border-[#2A2A2A] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-[#2A2A2A] bg-[#0A0A0A]">
            <tr>
              <th className="text-left p-4 smallcaps">Name</th>
              <th className="text-left p-4 smallcaps">SKU</th>
              <th className="text-left p-4 smallcaps">Category</th>
              <th className="text-right p-4 smallcaps">Price</th>
              <th className="text-right p-4 smallcaps">Stock</th>
              <th className="text-center p-4 smallcaps">Status</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {displayed.map((p) => (
              <tr key={p.id} className="border-b border-[#2A2A2A] hover:bg-[#0A0A0A]" data-testid={`row-${p.slug}`}>
                <td className="p-4 text-[#F5F5DC]">
                  <div className="flex items-center gap-2">
                    <span>{p.name}</span>
                    {p.on_sale && p.sale_price != null && p.sale_price < p.price && (
                      <span
                        className="text-[10px] uppercase tracking-widest text-[#F5F5DC] bg-[#8B1A1A] px-2 py-0.5"
                        data-testid={`sale-tag-${p.slug}`}
                        title="On sale"
                      >
                        Sale
                      </span>
                    )}
                    {remakeCounts[p.id] > 0 && (
                      <span
                        className="text-[10px] uppercase tracking-widest text-[#B87333] border border-[#B87333]/50 px-2 py-0.5"
                        data-testid={`remake-badge-${p.slug}`}
                        title={`${remakeCounts[p.id]} remake request${remakeCounts[p.id] === 1 ? '' : 's'}`}
                      >
                        ↻ {remakeCounts[p.id]}
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-4 text-[#A3A3A3] font-mono text-xs tracking-wider" data-testid={`row-sku-${p.slug}`}>{p.sku || "—"}</td>
                <td className="p-4 text-[#A3A3A3]">
                  {categoryLabel(p.category)}{p.subcategory ? ` · ${p.subcategory}` : ""}
                </td>
                <td className="p-4 text-right text-[#F5F5DC]">
                  {p.on_sale && p.sale_price != null && p.sale_price < p.price ? (
                    <>
                      <span className="text-[#A3A3A3] text-xs line-through mr-2">${p.price.toFixed(2)}</span>
                      <span className="text-[#8B1A1A]">${Number(p.sale_price).toFixed(2)}</span>
                    </>
                  ) : (
                    <>${p.price.toFixed(2)}</>
                  )}
                </td>
                <td className="p-4 text-right text-[#A3A3A3]">
                  {(p.stock ?? 0) < 1 ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#CF6679] border border-[#CF6679]/60 px-2 py-0.5 font-semibold" data-testid={`sold-out-badge-${p.slug}`}>
                      Sold Out
                    </span>
                  ) : (
                    p.stock
                  )}
                </td>
                <td className="p-4 text-center">
                  <span className={`text-xs uppercase tracking-widest ${p.active ? "text-[#4CAF50]" : "text-[#CF6679]"}`}>
                    {p.active ? "Active" : "Hidden"}
                  </span>
                </td>
                <td className="p-4 text-right">
                  <button onClick={() => startEdit(p)} className="text-[#A3A3A3] hover:text-[#B87333] mr-3" data-testid={`edit-${p.slug}`}><Edit2 size={14} /></button>
                  <button onClick={() => del(p)} className="text-[#A3A3A3] hover:text-[#CF6679]" data-testid={`delete-${p.slug}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {displayed.length === 0 && (
              <tr><td colSpan={7} className="p-12 text-center text-[#A3A3A3]" data-testid="empty-products">
                {skuFilter.trim() ? `No products with "${skuFilter.trim()}" in the code.` : (soldOutOnly ? "No sold-out products right now — nice." : "No products yet.")}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3">
        {displayed.map((p) => (
          <div key={p.id} className="bg-[#111] border border-[#2A2A2A] p-4 flex gap-4" data-testid={`row-mobile-${p.slug}`}>
            <div className="w-20 h-20 bg-[#161616] border border-[#2A2A2A] flex-shrink-0 overflow-hidden">
              {p.images?.[0] && <img src={fileUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-display text-lg text-[#F5F5DC] truncate">{p.name}</p>
                {(p.stock ?? 0) < 1 && (
                  <span className="text-[10px] uppercase tracking-widest text-[#CF6679] border border-[#CF6679]/60 px-2 py-0.5 font-semibold" data-testid={`sold-out-badge-m-${p.slug}`}>
                    Sold Out
                  </span>
                )}
                {remakeCounts[p.id] > 0 && (
                  <span
                    className="text-[10px] uppercase tracking-widest text-[#B87333] border border-[#B87333]/50 px-2 py-0.5"
                    data-testid={`remake-badge-m-${p.slug}`}
                  >
                    ↻ {remakeCounts[p.id]}
                  </span>
                )}
              </div>
              <p className="text-xs text-[#A3A3A3] mt-0.5">
                {categoryLabel(p.category)}{p.subcategory ? ` · ${p.subcategory}` : ""} · ${p.price.toFixed(2)} · {p.stock} in stock
              </p>
              <span className={`text-[10px] uppercase tracking-widest ${p.active ? "text-[#4CAF50]" : "text-[#CF6679]"}`}>
                {p.active ? "Active" : "Hidden"}
              </span>
              <div className="flex gap-4 mt-3">
                <button onClick={() => startEdit(p)} className="text-xs uppercase tracking-widest text-[#B87333]" data-testid={`edit-m-${p.slug}`}>
                  Edit
                </button>
                <button onClick={() => del(p)} className="text-xs uppercase tracking-widest text-[#A3A3A3]" data-testid={`delete-m-${p.slug}`}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
        {displayed.length === 0 && (
          <p className="text-center text-[#A3A3A3] py-12" data-testid="empty-products-mobile">
            {soldOutOnly ? "No sold-out products right now — nice." : "No products yet."}
          </p>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 flex items-stretch sm:items-center justify-center z-50 sm:p-4" data-testid="product-modal">
          <div className="bg-[#0A0A0A] sm:border sm:border-[#2A2A2A] sm:max-w-2xl w-full sm:max-h-[90vh] flex flex-col">
            <div className="p-4 sm:p-6 border-b border-[#2A2A2A] flex justify-between items-center sticky top-0 bg-[#0A0A0A] z-10">
              <h2 className="font-display text-xl sm:text-2xl text-[#F5F5DC]">{editing.id ? "Edit Product" : "New Product"}</h2>
              <button onClick={() => setEditing(null)} className="text-[#A3A3A3] p-2 -mr-2"><X size={22} /></button>
            </div>
            <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="smallcaps block mb-1">Name</label>
                  <input data-testid="pf-name" className="input-dark" value={editing.name ?? ""} onChange={(e) => onNameChange(e.target.value)} placeholder="e.g. Heritage Walnut Board" />
                </div>
                <div>
                  <label className="smallcaps block mb-1">URL Slug</label>
                  <input data-testid="pf-slug" className="input-dark" value={editing.slug ?? ""} onChange={(e) => set("slug", e.target.value)} placeholder="auto-filled from name" />
                  <p className="text-[10px] text-[#555] mt-1">Lowercase, hyphens only. Leave blank to auto-generate.</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="smallcaps block mb-1">Category</label>
                  <select data-testid="pf-cat" className="input-dark" value={editing.category ?? "chopping_board"} onChange={(e) => set("category", e.target.value)}>
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.singular}</option>)}
                  </select>
                </div>
                <div>
                  <label className="smallcaps block mb-1">Sub-category (optional)</label>
                  <input
                    data-testid="pf-subcat"
                    className="input-dark"
                    list="subcategory-suggestions"
                    placeholder="e.g. End-grain, Live-edge, Ocean"
                    value={editing.subcategory ?? ""}
                    onChange={(e) => set("subcategory", e.target.value)}
                  />
                  <datalist id="subcategory-suggestions">
                    {subcatSuggestions.map((s) => <option key={s} value={s} />)}
                  </datalist>
                  <p className="text-[10px] text-[#555] mt-1">Free text — used as a secondary filter on the shop page.</p>
                </div>
              </div>
              <div>
                <label className="smallcaps block mb-1">SKU / Product Code</label>
                <input
                  data-testid="pf-sku"
                  className="input-dark"
                  value={editing.sku ?? ""}
                  onChange={(e) => set("sku", e.target.value)}
                  placeholder="e.g. CB-WAL-001 — used to match physical inventory"
                />
                <p className="text-[10px] text-[#555] mt-1">Shown in the admin Orders table next to this item.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="smallcaps block mb-1">Price ($)</label><input data-testid="pf-price" type="number" step="0.01" className="input-dark" value={editing.price ?? 0} onChange={(e) => set("price", parseFloat(e.target.value) || 0)} /></div>
                <div><label className="smallcaps block mb-1">Stock</label><input type="number" className="input-dark" value={editing.stock ?? 0} onChange={(e) => set("stock", parseInt(e.target.value) || 0)} /></div>
              </div>
              <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-4">
                <label className="flex items-center gap-3 text-sm text-[#F5F5DC] cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="pf-on-sale"
                    checked={!!editing.on_sale}
                    onChange={(e) => set("on_sale", e.target.checked)}
                  />
                  <span className="smallcaps">On Sale</span>
                  <span className="text-xs text-[#A3A3A3] normal-case tracking-normal">— shows red SALE badge & strike-through original price</span>
                </label>
                {editing.on_sale && (
                  <div className="mt-3">
                    <label className="smallcaps block mb-1">Sale Price ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      data-testid="pf-sale-price"
                      className="input-dark"
                      value={editing.sale_price ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        set("sale_price", v === "" ? null : parseFloat(v));
                      }}
                      placeholder={`Must be lower than $${Number(editing.price || 0).toFixed(2)}`}
                    />
                    {editing.sale_price != null && Number(editing.sale_price) >= Number(editing.price || 0) && (
                      <p className="text-[11px] text-[#CF6679] mt-1">Sale price must be lower than the original price.</p>
                    )}
                  </div>
                )}
              </div>
              <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-4">
                <label className="flex items-center gap-3 text-sm text-[#F5F5DC] cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="pf-perfect-gift"
                    checked={!!editing.perfect_gift}
                    onChange={(e) => set("perfect_gift", e.target.checked)}
                  />
                  <span className="smallcaps">🎁 Perfect Gift</span>
                  <span className="text-xs text-[#A3A3A3] normal-case tracking-normal">— shows a gold &quot;Perfect Gift&quot; badge when a holiday campaign is active</span>
                </label>
              </div>

              {/* Smart Special — flips free shipping AND bumps price by $25 in lockstep */}
              <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-4">
                <div className="flex items-start gap-4 flex-wrap">
                  <button
                    type="button"
                    onClick={() => set("free_shipping", !editing.free_shipping)}
                    aria-pressed={!!editing.free_shipping}
                    data-testid="pf-free-shipping-toggle"
                    className={`inline-flex items-center gap-2 h-14 min-h-[48px] px-5 border-2 rounded-full transition-all active:scale-[0.97] touch-manipulation ${
                      editing.free_shipping
                        ? "bg-[#00BFA5] border-[#00BFA5] text-white shadow-lg"
                        : "bg-[#0A0A0A] border-[#2A2A2A] text-[#F5F5DC] hover:border-[#00BFA5]"
                    }`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                    <span className="font-display text-base font-bold tracking-wide">
                      {editing.free_shipping ? "Smart Special ON" : "Smart Special OFF"}
                    </span>
                  </button>
                  <p className="text-xs text-[#A3A3A3] flex-1 min-w-[180px] pt-1 leading-relaxed">
                    Turning this on bumps the price by <strong className="text-[#F5F5DC]">+$25</strong> and offers <strong className="text-[#00BFA5]">free shipping</strong> — the two always move together so you never eat the shipping cost. Turning it off restores the original price.
                  </p>
                </div>
              </div>
              <div>
                <label className="smallcaps block mb-1">Shipping Class</label>
                <select
                  data-testid="pf-shipping-class"
                  className="input-dark"
                  value={editing.shipping_class_id ?? ""}
                  onChange={(e) => set("shipping_class_id", e.target.value)}
                >
                  <option value="">— Use default —</option>
                  {shippingClasses.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} — ${c.price.toFixed(2)}{c.is_default ? " (default)" : ""}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-[#555] mt-1">
                  Determines shipping cost per unit for this product. Manage classes in <em>Admin → Shipping</em>.
                </p>
              </div>
              <div><label className="smallcaps block mb-1">Short Description</label><input data-testid="pf-short" className="input-dark" value={editing.short_description ?? ""} onChange={(e) => set("short_description", e.target.value)} placeholder="One-line tagline shown on cards" /></div>
              <div>
                <label className="smallcaps block mb-1">Description</label>
                <textarea data-testid="pf-desc" rows={8} className="input-dark resize-y min-h-[180px]" value={editing.description ?? ""} onChange={(e) => set("description", e.target.value)} placeholder="Tell the story of this piece — wood, grain, finish, what makes it special…" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="smallcaps block mb-1">Materials</label><input className="input-dark" value={editing.materials ?? ""} onChange={(e) => set("materials", e.target.value)} /></div>
                <div><label className="smallcaps block mb-1">Dimensions</label><input className="input-dark" value={editing.dimensions ?? ""} onChange={(e) => set("dimensions", e.target.value)} /></div>
              </div>
              <div><label className="smallcaps block mb-1">Stock</label><input type="number" className="input-dark" value={editing.stock ?? 0} onChange={(e) => set("stock", parseInt(e.target.value) || 0)} /></div>
              <div>
                <label className="smallcaps block mb-1">Images <span className="text-[10px] text-[#555] normal-case tracking-normal">(up to {MAX_IMAGES} — first one is the cover)</span></label>
                {(editing.images?.length || 0) < MAX_IMAGES ? (
                  <div className="flex flex-wrap gap-2 mb-3 items-center">
                    <input className="input-dark flex-1 min-w-[200px]" placeholder="Paste URL or upload below…" value={imgInput} onChange={(e) => setImgInput(e.target.value)} />
                    <button type="button" onClick={addImg} className="btn-secondary px-4 text-xs" data-testid="pf-add-img-url">Add URL</button>
                    <UploadButton
                      kind="image"
                      onUploaded={(url) => set("images", [...(editing.images || []), url].slice(0, MAX_IMAGES))}
                    />
                  </div>
                ) : (
                  <p className="text-[11px] text-[#B87333] mb-3" data-testid="pf-img-limit">Maximum of {MAX_IMAGES} images reached. Remove one to add another.</p>
                )}
                <div className="grid grid-cols-3 gap-2">
                  {Array.from({ length: MAX_IMAGES }).map((_, i) => {
                    const img = editing.images?.[i];
                    return (
                      <div key={i} className="relative group border border-[#2A2A2A] bg-[#111] aspect-square overflow-hidden" data-testid={`pf-img-slot-${i}`}>
                        {img ? (
                          <>
                            <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                            {i === 0 && (
                              <span className="absolute bottom-1 left-1 bg-[#0A0A0A]/90 text-[#B87333] text-[9px] uppercase tracking-widest px-2 py-0.5">Cover</span>
                            )}
                            <button
                              type="button"
                              onClick={() => rmImg(i)}
                              className="absolute top-1 right-1 bg-[#0A0A0A]/80 text-[#CF6679] p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`rm-img-${i}`}
                            >
                              <X size={14} />
                            </button>
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#444] text-xs uppercase tracking-widest">Slot {i + 1}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="smallcaps block mb-1">Product Video (optional)</label>
                <p className="text-xs text-[#A3A3A3] mb-2">A short clip shown on the product page. MP4 or WebM, max 30 MB.</p>
                <div className="flex flex-wrap gap-2 mb-2 items-center">
                  <input className="input-dark flex-1 min-w-[200px]" placeholder="Paste video URL or upload…" value={editing.video || ""} onChange={(e) => set("video", e.target.value)} data-testid="pf-video-url" />
                  <UploadButton kind="video" onUploaded={(url) => set("video", url)} />
                  {editing.video && (
                    <button type="button" onClick={() => set("video", "")} className="btn-ghost text-xs uppercase tracking-widest">Clear</button>
                  )}
                </div>
                {editing.video && (
                  <video src={fileUrl(editing.video)} controls muted className="w-full max-h-64 bg-[#111] border border-[#2A2A2A]" />
                )}
              </div>
              <div className="flex gap-6 pt-2">
                <label className="flex items-center gap-2 text-sm text-[#A3A3A3]"><input type="checkbox" checked={editing.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured</label>
                <label className="flex items-center gap-2 text-sm text-[#A3A3A3]"><input type="checkbox" checked={editing.active} onChange={(e) => set("active", e.target.checked)} /> Active</label>
              </div>
            </div>
            <div className="p-4 sm:p-6 border-t border-[#2A2A2A] flex justify-end gap-3 sticky bottom-0 bg-[#0A0A0A]">
              <button
                onClick={() => setEditing(null)}
                className="btn-ghost px-4"
                data-testid="pf-cancel"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={isSubmitting}
                className="btn-primary min-h-[56px] px-6 disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="pf-save"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving…
                  </>
                ) : (
                  editing?.id ? "Save" : "Create Product"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
